import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Maximize2 } from 'lucide-react';
import { MenuEngineeringDataPoint } from '../../../utils/chartsUtils';
import { MenuEngineeringMatrix } from '../../../services/costPrice/menuEngineeringService';
import { currencyFormatter } from '../../../services/helperService';
import { ExpandModal } from '../../ui/ExpandModal';
import { cardClass, primaryText, secondaryText, semiBoldText } from '../../../styling/stylingHelper';
import { cn } from '../../../lib/utils';

interface QuadrantTopCardsProps {
  data: MenuEngineeringDataPoint[];
}

type QuadrantKey = Exclude<MenuEngineeringMatrix, null>;

// Same quadrant colors used by the menu-engineering scatter chart.
const QUADRANTS: { key: QuadrantKey; color: string; labelKey: string }[] = [
  { key: 'Star', color: '#16B364', labelKey: 'costPercent.quadrant.stars' },
  { key: 'Plowhorse', color: '#F1A62D', labelKey: 'costPercent.quadrant.plowhorses' },
  { key: 'Puzzle', color: '#2E90FA', labelKey: 'costPercent.quadrant.puzzles' },
  { key: 'Dog', color: '#F04438', labelKey: 'costPercent.quadrant.dogs' },
];

const TOP_N = 5;

/**
 * Four cards (Stars / Plowhorses / Puzzles / Dogs), each listing the top 5
 * dishes in that quadrant by absolute € margin (menu price − cost price). Each
 * card can expand to a modal showing the full ranked list (like the dashboard
 * all-restaurants cards).
 */
export const QuadrantTopCards: React.FC<QuadrantTopCardsProps> = ({ data }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<QuadrantKey | null>(null);

  const byQuadrant = useMemo(() => {
    const map: Record<string, MenuEngineeringDataPoint[]> = { Star: [], Plowhorse: [], Puzzle: [], Dog: [] };
    data.forEach(d => { if (d.matrix && map[d.matrix]) map[d.matrix].push(d); });
    Object.keys(map).forEach(k => map[k].sort((a, b) => b.margin - a.margin));
    return map;
  }, [data]);

  const renderRows = (items: MenuEngineeringDataPoint[]) => (
    <ul className="flex flex-col gap-2">
      {items.map(item => (
        <li key={item.id} className="flex items-center justify-between gap-2">
          <span className={cn(primaryText, 'text-[13px] truncate')} title={item.name}>{item.name}</span>
          <span className={cn(secondaryText, semiBoldText, 'text-[13px] whitespace-nowrap')}>
            {currencyFormatter.format(item.margin)}
          </span>
        </li>
      ))}
    </ul>
  );

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {QUADRANTS.map(q => {
          const all = byQuadrant[q.key] || [];
          const items = all.slice(0, TOP_N);
          return (
            <div key={q.key} className={cn(cardClass, 'p-4 flex flex-col h-full')}>
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-[4px]" style={{ backgroundColor: q.color }} />
                  <span className={cn(primaryText, semiBoldText, 'text-[15px]')}>{t(q.labelKey)}</span>
                </div>
                {all.length > TOP_N && (
                  <button
                    type="button"
                    onClick={() => setExpanded(q.key)}
                    className="flex-none p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 transition"
                    aria-label={t('costPercent.quadrant.seeAll')}
                    title={t('costPercent.quadrant.seeAll')}
                  >
                    <Maximize2 className={cn('w-4 h-4', secondaryText)} />
                  </button>
                )}
              </div>
              {all.length === 0 ? (
                <p className={cn(secondaryText, 'text-[13px] py-4')}>{t('costPercent.quadrant.empty')}</p>
              ) : (
                renderRows(items)
              )}
            </div>
          );
        })}
      </div>

      <ExpandModal
        isOpen={expanded !== null}
        onClose={() => setExpanded(null)}
        title={expanded ? t(QUADRANTS.find(q => q.key === expanded)!.labelKey) : ''}
      >
        {expanded && renderRows(byQuadrant[expanded] || [])}
      </ExpandModal>
    </>
  );
};
